import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    TextField,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    FormControlLabel,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    InputAdornment,
    Card,
    CardContent,
    Grid,
    Radio,
    RadioGroup,
    IconButton,
    Alert,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import config from "../config";
import HeaderBar from "../components/HeaderBar";
import FundPieChart from "../components/FundPieChart";
import AddEditPurchaseModal from "../components/AddEditPurchaseModal";
import FundTrackerTour from "../components/FundTrackerTour";
import categoryFields from "../shared/categoryFields";
import themeColors from "../theme";
import ConferenceModal from "../components/ConferenceModal";
import FileUpload from "../components/FileUpload";
import DownloadIcon from "@mui/icons-material/Download";
import WalletIcon from "@mui/icons-material/AccountBalanceWallet";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../context/AuthContext";
import { isValidCost } from "../utils/validation";

// First-time orientation gate. Bump the version to re-show after changes.
const ONBOARDED_KEY = "fundTracker.onboarded.v1";

// ---------- helpers ----------
const asBool = (v) => v === true || v === "true" || v === 1 || v === "1";
const isConference = (cat) =>
    typeof cat === "string" && cat.toLowerCase().startsWith("conference");
const categoryOptions = Object.keys(categoryFields).sort();

// Extract year from purchase date
const getPurchaseYear = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    return d.getUTCFullYear();
};

const categoriesWithoutDate = [
    "Conferences",
    "Medical Society Membership Dues",
];

// ---------- CSV export helpers (no template strings) ----------
function escapeCsv(value) {
    if (value === null || value === undefined) return "";
    const s = String(value);
    if (
        s.indexOf('"') !== -1 ||
        s.indexOf(",") !== -1 ||
        s.indexOf("\n") !== -1 ||
        s.indexOf("\r") !== -1
    ) {
        return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
}

function rowForCsv(p, formatDate) {
    const category = p.category || "";
    const name = p.name || "-";
    const cost = typeof p.cost === "number" ? "$" + p.cost.toFixed(2) : "-";
    const date = formatDate(p.dateOfPurchase);
    const reimbursement = p.submittedForReimbursement ? "Submitted" : "Not Submitted";
    return [category, name, cost, date, reimbursement];
}

export default function FundTracker() {
    const { token } = useAuth();
    const [purchases, setPurchases] = useState([]);
    const [totalFunds, setTotalFunds] = useState("");
    const [rolloverEnabled, setRolloverEnabled] = useState(false);
    const [rolloverAmount, setRolloverAmount] = useState("");
    const [addingPurchase, setAddingPurchase] = useState(false);
    const [editingBudget, setEditingBudget] = useState(false);
    const [budgetError, setBudgetError] = useState("");
    const [addPurchaseError, setAddPurchaseError] = useState("");

    // Conference modals
    const [showConferenceModal, setShowConferenceModal] = useState(false); // create flow
    const [showConferenceEditModal, setShowConferenceEditModal] = useState(false); // edit flow

    const [newPurchase, setNewPurchase] = useState({
        category: "",
        name: "",
        cost: "",
        dateOfPurchase: "",
        metadata: {},
        file: null,
        submittedForReimbursement: false,
    });

    const [editOpen, setEditOpen] = useState(false); // non-Conference edit modal
    const [selectedPurchase, setSelectedPurchase] = useState(null);

    const [deleteConfirm, setDeleteConfirm] = useState({
        open: false,
        id: null,
        category: null,
    });

    const [yearConfirm, setYearConfirm] = useState({
        open: false,
        purchaseYear: null,
    });

    const [pendingConferenceSave, setPendingConferenceSave] = useState(null);
    const [loadingConferenceId, setLoadingConferenceId] = useState(null);

    const [autoInput, setAutoInput] = useState({}); // key -> inputValue

    // First-time orientation tour ---------------------------------------------
    const [tourStep, setTourStep] = useState(() =>
        localStorage.getItem(ONBOARDED_KEY) === "true" ? -1 : 0
    );
    const tourActive = tourStep >= 0 && tourStep < 3;
    const fundSettingsRef = useRef(null);
    const [spotlightRect, setSpotlightRect] = useState(null);
    const addExpenseRef = useRef(null);
    const scrollToAddExpenseRef = useRef(false);

    // Dev mode for testing recurring payments
    const [isDevMode, setIsDevMode] = useState(false);
    const [devTestDate, setDevTestDate] = useState("");
    const [devTestResult, setDevTestResult] = useState(null);
    const [devRolloverTestDate, setDevRolloverTestDate] = useState("");
    const [devRolloverResult, setDevRolloverResult] = useState(null);
    const currentYear = new Date().getFullYear();
    const [evaluationYear, setEvaluationYear] = useState(currentYear);
    const [totalAvailable, setTotalAvailable] = useState(0);

    // Table filters
    const [filters, setFilters] = useState({
        category: "",
        dateFrom: "",
        dateTo: "",
        costMin: "",
        costMax: "",
        reimbursement: "",
    });

    const resetFilters = () =>
        setFilters({
            category: "",
            dateFrom: "",
            dateTo: "",
            costMin: "",
            costMax: "",
            reimbursement: "",
        });

    const hasActiveFilters = Object.values(filters).some((v) => v !== "");


    // Check for ?dev=true in URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setIsDevMode(params.get("dev") === "true");
    }, []);

    // Summary values follow the real year normally and the simulated year in dev mode.
    const evaluatedYearPurchases = purchases.filter((p) => {
        const purchaseYear = getPurchaseYear(p.dateOfPurchase || p.createdAt);
        return purchaseYear === evaluationYear;
    });

    const filteredPurchases = purchases.filter((p) => {
        if (filters.category && p.category !== filters.category) return false;

        if (filters.reimbursement) {
            const submitted = !!p.submittedForReimbursement;
            if (filters.reimbursement === "submitted" && !submitted) return false;
            if (filters.reimbursement === "not_submitted" && submitted) return false;
        }

        if (filters.dateFrom || filters.dateTo) {
            if (!p.dateOfPurchase) return false;
            const d = new Date(p.dateOfPurchase);
            if (filters.dateFrom && d < new Date(filters.dateFrom)) return false;
            if (filters.dateTo && d > new Date(filters.dateTo + "T23:59:59")) return false;
        }

        if (filters.costMin !== "" && (p.cost ?? 0) < parseFloat(filters.costMin)) return false;
        if (filters.costMax !== "" && (p.cost ?? 0) > parseFloat(filters.costMax)) return false;

        return true;
    });

    const spent = evaluatedYearPurchases.reduce((acc, p) => acc + (p.cost || 0), 0);
    const remaining = totalAvailable - spent;

    const fetchPurchases = useCallback(async () => {
        try {
            const res = await axios.get(`${config.SERVER_URL}/api/purchases`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPurchases(res.data);
        } catch (err) {
            console.error("Failed to fetch purchases", err);
        }
    }, [token]);

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const d = new Date(dateString);
        const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(d.getUTCDate()).padStart(2, "0");
        const yyyy = d.getUTCFullYear();
        return mm + "/" + dd + "/" + yyyy;
    };

    const fetchBudget = useCallback(async () => {
        try {
            const res = await axios.get(`${config.SERVER_URL}/api/cmeBudget`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTotalFunds(res.data.cmeAmount);
            setRolloverAmount(res.data.rolloverAmount);
            setRolloverEnabled(!!res.data.rolloverAmount);
            setEvaluationYear(res.data.evaluationYear);
            setTotalAvailable(res.data.totalBudget);
        } catch (err) {
            console.error("Failed to fetch CME Budget", err);
        }
    }, [token]);

    useEffect(() => {
        fetchPurchases();
        fetchBudget();
    }, [fetchPurchases, fetchBudget]);

    // Spotlight the Fund Settings card during step 1 of the tour.
    useEffect(() => {
        if (!tourActive || tourStep !== 0) {
            setSpotlightRect(null);
            return;
        }
        const measure = () => {
            const node = fundSettingsRef.current;
            if (node) setSpotlightRect(node.getBoundingClientRect());
        };
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [tourActive, tourStep]);

    const finishTour = useCallback(() => {
        localStorage.setItem(ONBOARDED_KEY, "true");
        setTourStep(-1);
    }, []);

    const handleTourNext = useCallback(() => setTourStep((prev) => prev + 1), []);

    const handleTourSaveFund = useCallback(async (amount) => {
        await axios.post(
            `${config.SERVER_URL}/api/cmeBudget/cme-amount`,
            { cmeAmount: amount },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        await fetchBudget();
        setBudgetError("");
    }, [fetchBudget, token]);

    const handleTourSaveRollover = useCallback(async (amount) => {
        await axios.post(
            `${config.SERVER_URL}/api/cmeBudget/rollover`,
            {
                rolloverAmount: amount,
                rolloverYear: currentYear,
            },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        setRolloverAmount(amount);
        setRolloverEnabled(amount > 0);
        await fetchBudget();
        setBudgetError("");
    }, [currentYear, fetchBudget, token]);

    const handleExportCsv = () => {
        const header = ["Category", "Name", "Cost", "Date", "Reimbursement Status"];
        const lines = [];
        lines.push(header.map(escapeCsv).join(","));
        for (const p of purchases) {
            lines.push(rowForCsv(p, formatDate).map(escapeCsv).join(","));
        }
        const csv = lines.join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        a.href = url;
        a.download = "cme-expenses-" + yyyy + "-" + mm + "-" + dd + ".csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleSaveBudget = async () => {
        setBudgetError("");

        // Validate: if rollover is enabled, amount must be provided and > 0
        if (rolloverEnabled && (!rolloverAmount || parseFloat(rolloverAmount) <= 0)) {
            setBudgetError("Please enter a rollover amount greater than 0.");
            return;
        }

        try {
            await axios.post(
                `${config.SERVER_URL}/api/cmeBudget/cme-amount`,
                { cmeAmount: parseFloat(totalFunds) || 0 },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (rolloverEnabled && rolloverAmount) {
                await axios.post(
                    `${config.SERVER_URL}/api/cmeBudget/rollover`,
                    {
                        rolloverAmount: parseFloat(rolloverAmount) || 0,
                        rolloverYear: new Date().getFullYear(),
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } else if (!rolloverEnabled) {
                await axios.post(
                    `${config.SERVER_URL}/api/cmeBudget/rollover`,
                    {
                        rolloverAmount: 0,
                        rolloverYear: new Date().getFullYear(),
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }

            setEditingBudget(false);
            await fetchBudget();
        } catch (err) {
            console.error("Failed to save budget", err);
        }
    };

    // ---------- DEV: Test Recurring Payments ----------
    const handleTestRecurring = async () => {
        try {
            setDevTestResult({ loading: true });

            const url = devTestDate
                ? `${config.SERVER_URL}/__dev/recurring?testDate=${devTestDate}`
                : `${config.SERVER_URL}/__dev/recurring`;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDevTestResult({ success: true, data: res.data });

            // Refresh purchases to show newly created instances
            await fetchPurchases();
        } catch (err) {
            setDevTestResult({
                success: false,
                error: err.response?.data?.error || err.message
            });
        }
    };

    // ---------- DEV: Evaluate rollover as of a simulated date ----------
    const handleTestRolloverDate = async () => {
        try {
            setDevRolloverResult({ loading: true });
            const res = await axios.get(`${config.SERVER_URL}/api/cmeBudget`, {
                params: devRolloverTestDate ? { testDate: devRolloverTestDate } : {},
                headers: { Authorization: `Bearer ${token}` },
            });
            setDevRolloverResult({
                success: true,
                label: `Evaluated as of ${devRolloverTestDate || "today"}`,
                data: {
                    rolloverYear: res.data.rolloverYear,
                    currentYear: res.data.evaluationYear,
                    rolloverApplies: res.data.rolloverApplies,
                    totalBudget: res.data.totalBudget,
                },
            });
            setEvaluationYear(res.data.evaluationYear);
            setTotalAvailable(res.data.totalBudget);
        } catch (err) {
            setDevRolloverResult({
                success: false,
                error: err.response?.data?.error || err.message,
            });
        }
    };

    // ---------- ADD (non-Conference) ----------
    const handleAddPurchase = async (skipYearCheck = false) => {
        setAddPurchaseError("");

        // Validation
        if (!newPurchase.category) {
            setAddPurchaseError("Please select a category.");
            return;
        }

        // If the user picked Conferences, redirect to Conference modal path
        if (newPurchase.category === "Conferences") {
            setShowConferenceModal(true);
            return;
        }

        // Validate cost
        if (!newPurchase.cost || !isValidCost(newPurchase.cost) || parseFloat(newPurchase.cost) <= 0) {
            setAddPurchaseError("Please enter a valid cost between $0.01 and $100,000.");
            return;
        }

        // Validate date for categories that require it
        if (!categoriesWithoutDate.includes(newPurchase.category)) {
            if (!newPurchase.dateOfPurchase) {
                setAddPurchaseError("Please select a date for this purchase.");
                return;
            }
        }

        // Check if purchase date is outside current year
        if (!skipYearCheck && newPurchase.dateOfPurchase && !categoriesWithoutDate.includes(newPurchase.category)) {
            const purchaseYear = getPurchaseYear(newPurchase.dateOfPurchase);
            if (purchaseYear !== currentYear) {
                setYearConfirm({ open: true, purchaseYear });
                return;
            }
        }

        const formData = new FormData();
        formData.append("category", newPurchase.category);
        formData.append("cost", newPurchase.cost);
        if (!categoriesWithoutDate.includes(newPurchase.category)) {
            formData.append("dateOfPurchase", newPurchase.dateOfPurchase);
        }
        if (newPurchase.name) formData.append("name", newPurchase.name);
        if (newPurchase.file) formData.append("file", newPurchase.file);
        formData.append(
            "submittedForReimbursement",
            String(!!newPurchase.submittedForReimbursement)
        );

        // ensure we don't leak this as a primitive field
        const meta = { ...(newPurchase.metadata || {}) };
        delete meta.submittedForReimbursement;

        const isRecurring = asBool(meta?.recurring);

        // Append everything except recurring keys (handled below)
        Object.entries(meta).forEach(([key, value]) => {
            if (key === "recurring" || key === "recurringCycleMonths") return;
            if (value === undefined || value === null) return;
            if (value === "" && typeof value !== "boolean") return;
            formData.append(key, typeof value === "boolean" ? String(value) : value);
        });

        // Only send recurring + cycle if checked
        if (isRecurring) {
            formData.append("recurring", "true");
            const cycle = meta?.recurringCycleMonths;
            if (cycle !== undefined && cycle !== null && cycle !== "") {
                formData.append("recurringCycleMonths", String(cycle));
            }
        }

        try {
            await axios.post(`${config.SERVER_URL}/api/purchases`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchPurchases();
            setAddingPurchase(false);
            resetNewPurchase();
            setAddPurchaseError("");
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.response?.data?.message || "Failed to add purchase. Please check all required fields.";
            setAddPurchaseError(errorMsg);
            console.error(err);
        }
    };

    const resetNewPurchase = () => {
        setNewPurchase({
            category: "",
            name: "",
            cost: "",
            dateOfPurchase: "",
            submittedForReimbursement: false,
            metadata: {},
            file: null,
        });
        setAutoInput({});
        setAddPurchaseError("");
    };

    const handleTourSelectCategory = useCallback((category) => {
        finishTour();
        setAddPurchaseError("");
        setAutoInput({});

        if (category === "Conferences") {
            setAddingPurchase(false);
            setNewPurchase({
                category,
                name: "",
                cost: "",
                dateOfPurchase: "",
                submittedForReimbursement: false,
                metadata: {},
                file: null,
            });
            setShowConferenceModal(true);
            return;
        }

        setShowConferenceModal(false);
        scrollToAddExpenseRef.current = true;
        setAddingPurchase(true);
        setNewPurchase({
            category,
            name: "",
            cost: "",
            dateOfPurchase: "",
            submittedForReimbursement: false,
            metadata: {},
            file: null,
        });
    }, [finishTour]);

    // After the tour's category pick opens the Add New Expense form, bring it
    // into view — the form renders well below the fold on first visit.
    useEffect(() => {
        if (!addingPurchase || !scrollToAddExpenseRef.current) return;
        scrollToAddExpenseRef.current = false;
        addExpenseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [addingPurchase]);

    // ---------- DELETE (table-level) ----------
    const handleDeleteConfirmed = async () => {
        try {
            const url = isConference(deleteConfirm.category)
                ? `${config.SERVER_URL}/api/conference/${deleteConfirm.id}`
                : `${config.SERVER_URL}/api/purchases/${deleteConfirm.id}`;
            await axios.delete(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchPurchases();
            setDeleteConfirm({ open: false, id: null, category: null });
        } catch (err) {
            alert("Failed to delete purchase.");
        }
    };

    const loadConferencePurchase = async (purchase) => {
        const targetId = purchase?._id;
        if (!targetId || loadingConferenceId === targetId) return;
        setLoadingConferenceId(targetId);
        setSelectedPurchase(null);
        try {
            const res = await axios.get(`${config.SERVER_URL}/api/conference/${targetId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const conferencePurchase = res.data?.purchase;
            if (!conferencePurchase) {
                throw new Error("Failed to load conference purchase.");
            }
            setSelectedPurchase(conferencePurchase);
            setShowConferenceEditModal(true);
        } catch (err) {
            const message = err.response?.data?.error || err.message || "Failed to load conference purchase.";
            alert(message);
            console.error(err);
        } finally {
            setLoadingConferenceId((current) => (current === targetId ? null : current));
        }
    };

    const handleEditPurchase = (purchase) => {
        if (isConference(purchase.category)) {
            loadConferencePurchase(purchase);
            return;
        }
        setSelectedPurchase(purchase);
        setEditOpen(true);
    };

    // ---------- CHART BREAKDOWN ----------
    // Use purchases from the active real or simulated year for the chart.
    const categoryBreakdown = evaluatedYearPurchases.reduce((acc, p) => {
        if (!p.category || typeof p.cost !== "number") return acc;
        const existing = acc.find((item) => item.name === p.category);
        if (existing) {
            existing.value += p.cost;
        } else {
            acc.push({ name: p.category, value: p.cost });
        }
        return acc;
    }, []);

    const today = new Date();
    const localToday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
    )
        .toISOString()
        .split("T")[0];

    // ---------- CONFERENCE CREATE (modal) ----------
    // ConferenceModal passes { metadata, dateOfPurchase, files, mode } (files: {key->File})
    const handleConferenceSave = async ({ id, dateOfPurchase, metadata, files, mode }, skipYearCheck = false) => {
        // Check if purchase date is outside current year
        if (!skipYearCheck && dateOfPurchase) {
            const purchaseYear = getPurchaseYear(dateOfPurchase);
            if (purchaseYear !== currentYear) {
                setPendingConferenceSave({ id, dateOfPurchase, metadata, files, mode });
                setYearConfirm({ open: true, purchaseYear });
                return;
            }
        }

        const form = new FormData();
        form.append("metadata", JSON.stringify(metadata));
        if (dateOfPurchase) form.append("dateOfPurchase", dateOfPurchase);

        Object.entries(files || {}).forEach(([key, file]) => {
            form.append("files." + key, file, file.name);
        });

        const url =
            mode === "edit" && id
                ? `${config.SERVER_URL}/api/conference/${id}`
                : `${config.SERVER_URL}/api/conference`;
        const method = mode === "edit" && id ? "put" : "post";

        await axios[method](url, form, {
            headers: { Authorization: `Bearer ${token}` },
        });

        await fetchPurchases();
        setShowConferenceModal(false);
        setShowConferenceEditModal(false);
        setAddingPurchase(false);
        resetNewPurchase();
        setPendingConferenceSave(null);
    };

    // ---------- CONFERENCE EDIT (modal) ----------
    const handleConferenceEditSave = async ({ id, dateOfPurchase, metadata, files }, skipYearCheck = false) => {
        if (!id) {
            alert("Missing conference id.");
            return;
        }

        // Check if purchase date is outside current year
        if (!skipYearCheck && dateOfPurchase) {
            const purchaseYear = getPurchaseYear(dateOfPurchase);
            if (purchaseYear !== currentYear) {
                setPendingConferenceSave({ id, dateOfPurchase, metadata, files, mode: "edit" });
                setYearConfirm({ open: true, purchaseYear });
                return;
            }
        }

        try {
            const form = new FormData();
            form.append("metadata", JSON.stringify(metadata));
            if (dateOfPurchase) form.append("dateOfPurchase", dateOfPurchase);

            Object.entries(files || {}).forEach(([key, file]) => {
                if (file) form.append("files." + key, file, file.name);
            });

            await axios.put(`${config.SERVER_URL}/api/conference/${id}`, form, {
                headers: { Authorization: `Bearer ${token}` },
            });

            await fetchPurchases();
            setShowConferenceEditModal(false);
            setSelectedPurchase(null);
            setPendingConferenceSave(null);
        } catch (err) {
            alert("Failed to update conference purchase.");
            console.error(err);
        }
    };

    const handleConferenceEditDelete = async (id) => {
        try {
            await axios.delete(`${config.SERVER_URL}/api/conference/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            await fetchPurchases();
            setShowConferenceEditModal(false);
            setSelectedPurchase(null);
        } catch (err) {
            alert("Failed to delete conference purchase.");
            console.error(err);
        }
    };

    return (
        <>
            <HeaderBar />
            <Box sx={{ p: 4, backgroundColor: "#F5F5F5", minHeight: "100vh" }}>
                {/* Page Header */}
                <Typography variant="h3" fontWeight="bold" color="#2C3E50" mb={1}>
                    CME Fund Tracker
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={4}>
                    Track spending, reimbursements, and remaining CME funds.
                </Typography>

                {/* Top Row: Fund Settings + Summary Cards */}
                <Grid container spacing={3}>
                    {/* Fund Settings */}
                    <Grid item xs={12} md={3}>
                        <Card ref={fundSettingsRef} sx={{ borderRadius: 2, boxShadow: 1 }}>
                            <CardContent>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                    <Typography variant="h6" fontWeight="600">
                                        Fund Settings
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setEditingBudget(!editingBudget);
                                            if (budgetError) setBudgetError("");
                                        }}
                                        sx={{ color: themeColors.teal }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                {editingBudget ? (
                                    <>
                                        <TextField
                                            fullWidth
                                            label="Annual CME Fund"
                                            type="number"
                                            value={totalFunds}
                                            size="small"
                                            slotProps={{
                                                htmlInput: { min: 0 },
                                                input: {
                                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "-" || e.key === "e") e.preventDefault();
                                            }}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === "" || parseFloat(val) >= 0) setTotalFunds(val);
                                            }}
                                            sx={{ mb: 2 }}
                                        />

                                        <Typography variant="body2" fontWeight="600" mb={1}>
                                            Rollover allowed
                                        </Typography>
                                        <RadioGroup
                                            value={rolloverEnabled ? "yes" : "no"}
                                            onChange={(e) => {
                                                setRolloverEnabled(e.target.value === "yes");
                                                if (budgetError) setBudgetError("");
                                            }}
                                        >
                                            <FormControlLabel
                                                value="yes"
                                                control={<Radio size="small" />}
                                                label={<Typography variant="body2">Yes</Typography>}
                                            />
                                            <FormControlLabel
                                                value="no"
                                                control={<Radio size="small" />}
                                                label={<Typography variant="body2">No</Typography>}
                                            />
                                        </RadioGroup>

                                        {rolloverEnabled && (
                                            <TextField
                                                fullWidth
                                                label="Rollover Amount"
                                                type="number"
                                                value={rolloverAmount}
                                                size="small"
                                                required
                                                error={rolloverEnabled && budgetError !== ""}
                                                slotProps={{
                                                    htmlInput: { min: 0 },
                                                    input: {
                                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "-" || e.key === "e") e.preventDefault();
                                                }}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === "" || parseFloat(val) >= 0) setRolloverAmount(val);
                                                    if (budgetError) setBudgetError("");
                                                }}
                                                sx={{ mt: 1 }}
                                            />
                                        )}

                                        {budgetError && (
                                            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setBudgetError("")}>
                                                {budgetError}
                                            </Alert>
                                        )}

                                        <Button
                                            fullWidth
                                            variant="contained"
                                            size="small"
                                            onClick={handleSaveBudget}
                                            sx={{
                                                mt: 2,
                                                backgroundColor: themeColors.teal,
                                                '&:hover': { backgroundColor: themeColors.darkTeal },
                                                textTransform: 'none'
                                            }}
                                        >
                                            Save
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Annual CME Fund
                                        </Typography>
                                        <Typography variant="h5" fontWeight="600" mb={2}>
                                            $ {parseFloat(totalFunds || 0).toLocaleString()}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary" mb={1}>
                                            Rollover allowed
                                        </Typography>
                                        <Typography variant="body1" mb={rolloverEnabled ? 1 : 0}>
                                            {rolloverEnabled ? "Yes" : "No"}
                                        </Typography>
                                        {rolloverEnabled && rolloverAmount > 0 && (
                                            <>
                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                    Rollover Amount
                                                </Typography>
                                                <Typography variant="body1">
                                                    $ {parseFloat(rolloverAmount || 0).toLocaleString()}
                                                </Typography>
                                            </>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Summary Cards */}
                    <Grid item xs={12} md={9}>
                        <Grid container spacing={2}>
                            {/* Total Fund Card */}
                            <Grid item xs={12} sm={4}>
                                <Card sx={{ borderRadius: 2, boxShadow: 1, height: '100%' }}>
                                    <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: '100%' }}>
                                            <Box
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    borderRadius: 2,
                                                    backgroundColor: "#E0F2F1",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <WalletIcon sx={{ color: themeColors.teal, fontSize: 28 }} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Fund
                                                </Typography>
                                                <Typography variant="h5" fontWeight="600">
                                                    ${totalAvailable.toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Spent Card */}
                            <Grid item xs={12} sm={4}>
                                <Card sx={{ borderRadius: 2, boxShadow: 1, height: '100%' }}>
                                    <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: '100%' }}>
                                            <Box
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    borderRadius: 2,
                                                    backgroundColor: "#FFF9C4",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <MonetizationOnIcon sx={{ color: "#F9A825", fontSize: 28 }} />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Spent
                                                </Typography>
                                                <Typography variant="h5" fontWeight="600">
                                                    ${spent.toLocaleString()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Remaining Card */}
                            <Grid item xs={12} sm={4}>
                                <Card sx={{ borderRadius: 2, boxShadow: 1, height: '100%' }}>
                                    <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: '100%' }}>
                                            <Box
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    borderRadius: 2,
                                                    backgroundColor: remaining < 0 ? "#FFEBEE" : "#E8F5E9",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <ErrorOutlineIcon
                                                    sx={{
                                                        color: remaining < 0 ? "#D32F2F" : "#43A047",
                                                        fontSize: 28
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Remaining
                                                </Typography>
                                                <Typography
                                                    variant="h5"
                                                    fontWeight="600"
                                                    color={remaining < 0 ? "#D32F2F" : "text.primary"}
                                                >
                                                    {remaining < 0 ? "-" : ""}${Math.abs(remaining).toLocaleString()}
                                                </Typography>
                                                {remaining < 0 && (
                                                    <Typography variant="caption" color="#D32F2F" display="block">
                                                        Over budget by ${Math.abs(remaining).toLocaleString()}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Chart and Action Buttons */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                    <Card sx={{ borderRadius: 2, boxShadow: 1, maxWidth: 600, width: "100%" }}>
                        <CardContent>
                            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                                {totalAvailable > 0 && (
                                    <FundPieChart categoryBreakdown={categoryBreakdown} remaining={remaining} />
                                )}
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setAddingPurchase(true)}
                            sx={{
                                backgroundColor: themeColors.teal,
                                '&:hover': { backgroundColor: themeColors.darkTeal },
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            LOG EXPENSE
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleExportCsv}
                            sx={{
                                borderColor: themeColors.teal,
                                color: themeColors.teal,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                    borderColor: themeColors.darkTeal,
                                    backgroundColor: 'rgba(0, 127, 126, 0.04)',
                                },
                            }}
                        >
                            EXPORT CSV
                        </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* DEV MODE: Test Recurring Payments */}
                {isDevMode && (
                    <Box sx={{ mb: 4, p: 3, border: "2px dashed #ff9800", borderRadius: 2, backgroundColor: "#fff3e0" }}>
                        <Typography variant="h6" color="#e65100" mb={2}>
                            🧪 Dev Mode: Test Recurring Payments
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Simulate processing recurring payments on a future date. Leave date empty to use today.
                        </Typography>
                        <Box display="flex" gap={2} alignItems="center" justifyContent="center" mb={2}>
                            <TextField
                                type="date"
                                label="Test Date (optional)"
                                value={devTestDate}
                                onChange={(e) => setDevTestDate(e.target.value)}
                                slotProps={{ inputLabel: { shrink: true } }}
                                helperText="Simulate running recurring payment processing on this date"
                                sx={{ minWidth: 250 }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleTestRecurring}
                                disabled={devTestResult?.loading}
                                sx={{ backgroundColor: "#ff9800", color: "#fff", height: 56 }}
                            >
                                {devTestResult?.loading ? "Processing..." : "Run Test"}
                            </Button>
                        </Box>
                        {devTestResult && !devTestResult.loading && (
                            <Box sx={{ mt: 2, p: 2, borderRadius: 1, backgroundColor: devTestResult.success ? "#e8f5e9" : "#ffebee" }}>
                                {devTestResult.success ? (
                                    <>
                                        <Typography variant="body2" color="success.dark">
                                            ✅ {devTestResult.data.message}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Mode: {devTestResult.data.mode} | Simulated Date: {devTestResult.data.simulatedDate}
                                            {` | Recurring templates: ${devTestResult.data.templatesFound} | Payments created: ${devTestResult.data.instancesCreated}`}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="body2" color="error.dark">
                                        ❌ Error: {devTestResult.error}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                {/* DEV MODE: Test Rollover */}
                {isDevMode && (
                    <Box sx={{ mb: 4, p: 3, border: "2px dashed #ff9800", borderRadius: 2, backgroundColor: "#fff3e0" }}>
                        <Typography variant="h6" color="#e65100" mb={2}>
                            🧪 Dev Mode: Test Rollover
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Pick a test date (e.g. {currentYear + 1}-01-01) and the tracker will show the fund,
                            spending, remaining balance, and chart for that year. A rollover recorded for {currentYear}
                            is added to the {currentYear + 1} annual fund. Testing a date does not change saved data.
                        </Typography>
                        <Box display="flex" gap={2} alignItems="center" justifyContent="center" mb={2} flexWrap="wrap">
                            <TextField
                                type="date"
                                label="Rollover Test Date"
                                value={devRolloverTestDate}
                                onChange={(e) => setDevRolloverTestDate(e.target.value)}
                                slotProps={{ inputLabel: { shrink: true } }}
                                helperText={`Evaluate the rollover as of this date, e.g. ${currentYear + 1}-01-01 to test ${currentYear + 1}`}
                                sx={{ minWidth: 250 }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleTestRolloverDate}
                                disabled={devRolloverResult?.loading}
                                sx={{ backgroundColor: "#ff9800", color: "#fff", height: 56 }}
                            >
                                Test date
                            </Button>
                        </Box>
                        {devRolloverResult && !devRolloverResult.loading && (
                            <Box sx={{ mt: 2, p: 2, borderRadius: 1, backgroundColor: devRolloverResult.success ? "#e8f5e9" : "#ffebee" }}>
                                {devRolloverResult.success ? (
                                    <>
                                        <Typography variant="body2" color="success.dark">
                                            ✅ {devRolloverResult.label} — rolloverApplies = {String(devRolloverResult.data.rolloverApplies)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            rolloverYear: {devRolloverResult.data.rolloverYear} | viewingYear: {devRolloverResult.data.currentYear} | totalBudget: ${devRolloverResult.data.totalBudget}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="body2" color="error.dark">
                                        ❌ Error: {devRolloverResult.error}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                {/* ADD FLOW (non-Conference) */}
                {addingPurchase && (
                    <Card ref={addExpenseRef} sx={{ borderRadius: 2, boxShadow: 1, mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" fontWeight="600" mb={3}>
                                Add New Expense
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={2} maxWidth="500px" mx="auto">
                                <TextField
                                    select
                                    label="Category"
                                    value={newPurchase.category}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        setNewPurchase({ ...newPurchase, category: next });
                                        if (next === "Conferences") setShowConferenceModal(true);
                                    }}
                                    fullWidth
                                    size="small"
                                >
                                    {categoryOptions.map((c) => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {newPurchase.category &&
                                    newPurchase.category !== "Conferences" &&
                                    (categoryFields[newPurchase.category] || []).map((field) => {
                                        const { key, label, type, options, freeSoloAllowed = false } = field;

                                        if (key === "dateOfPurchase" && categoriesWithoutDate.includes(newPurchase.category)) {
                                            return null;
                                        }

                                        if (type === "autocomplete") {
                                            const opts = options || [];
                                            const val = key === "name" ? newPurchase.name || "" : newPurchase.metadata?.[key] || "";

                                            const setVal = (next) => {
                                                if (key === "name") {
                                                    setNewPurchase((prev) => ({ ...prev, name: next }));
                                                } else {
                                                    setNewPurchase((prev) => ({
                                                        ...prev,
                                                        metadata: { ...prev.metadata, [key]: next },
                                                    }));
                                                }
                                            };

                                            const inputVal = autoInput[key] ?? (val || "");

                                            return (
                                                <Autocomplete
                                                    key={key}
                                                    fullWidth
                                                    options={opts}
                                                    freeSolo={freeSoloAllowed}
                                                    value={val || null}
                                                    inputValue={inputVal}
                                                    isOptionEqualToValue={(opt, v) => opt === v}
                                                    onInputChange={(_, newInput) => {
                                                        setAutoInput((prev) => ({ ...prev, [key]: newInput || "" }));
                                                        if (freeSoloAllowed) setVal(newInput || "");
                                                    }}
                                                    onChange={(_, newValue) => {
                                                        const finalVal = newValue || "";
                                                        setVal(finalVal);
                                                        setAutoInput((prev) => ({ ...prev, [key]: finalVal }));
                                                    }}
                                                    renderInput={(params) => <TextField {...params} label={label} fullWidth size="small" />}
                                                />
                                            );
                                        }

                                        if (type === "checkbox") {
                                            const checked = asBool(newPurchase.metadata?.[key]);
                                            return (
                                                <FormControlLabel
                                                    key={key}
                                                    control={
                                                        <Checkbox
                                                            checked={!!checked}
                                                            onChange={(e) => {
                                                                const v = e.target.checked;
                                                                setNewPurchase((prev) => {
                                                                    const next = { ...prev, metadata: { ...prev.metadata, [key]: v } };
                                                                    if (key === "recurring" && !v) delete next.metadata.recurringCycleMonths;
                                                                    return next;
                                                                });
                                                            }}
                                                        />
                                                    }
                                                    label={label}
                                                />
                                            );
                                        }

                                        if (type === "select") {
                                            if (key === "recurringCycleMonths" && !asBool(newPurchase.metadata?.recurring)) {
                                                return null;
                                            }

                                            const value = newPurchase.metadata?.[key] ?? "";
                                            return (
                                                <TextField
                                                    key={key}
                                                    select
                                                    label={label}
                                                    value={value}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        setNewPurchase((prev) => ({
                                                            ...prev,
                                                            metadata: { ...prev.metadata, [key]: v },
                                                        }));
                                                    }}
                                                    fullWidth
                                                    size="small"
                                                >
                                                    {(options || []).map((opt) => (
                                                        <MenuItem key={opt} value={opt}>
                                                            {opt}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            );
                                        }

                                        const inputProps =
                                            key === "cost" ? { min: 0 } : key === "dateOfPurchase" ? { max: localToday } : undefined;

                                        const value =
                                            key === "name"
                                                ? newPurchase.name
                                                : key === "cost"
                                                    ? newPurchase.cost
                                                    : key === "dateOfPurchase"
                                                        ? newPurchase.dateOfPurchase
                                                        : newPurchase.metadata[key] || "";

                                        return (
                                            <TextField
                                                key={key}
                                                label={label}
                                                type={type}
                                                slotProps={{
                                                    inputLabel: type === "date" ? { shrink: true } : undefined,
                                                    htmlInput: inputProps
                                                }}
                                                onKeyDown={(e) => {
                                                    if ((key === "cost" || key === "dateOfPurchase") && (e.key === "-" || e.key === "e")) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                value={value}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (key === "cost") {
                                                        if (val === "" || (!isNaN(val) && parseFloat(val) >= 0)) {
                                                            setNewPurchase({ ...newPurchase, cost: val });
                                                        }
                                                    } else if (key === "dateOfPurchase") {
                                                        setNewPurchase({ ...newPurchase, dateOfPurchase: val });
                                                    } else if (key === "name") {
                                                        setNewPurchase({ ...newPurchase, name: val });
                                                    } else {
                                                        if (key === "submittedForReimbursement") return;
                                                        setNewPurchase({
                                                            ...newPurchase,
                                                            metadata: { ...newPurchase.metadata, [key]: val },
                                                        });
                                                    }
                                                }}
                                                fullWidth
                                                size="small"
                                            />
                                        );
                                    })}

                                {/* Single-file upload & reimbursement checkbox for non-Conference only */}
                                {newPurchase.category && newPurchase.category !== "Conferences" && (
                                    <>
                                        <FileUpload
                                            label="Attach receipt"
                                            multiple={false}
                                            buttonSx={{ backgroundColor: themeColors.teal, color: "#fff" }}
                                            onFilesChange={(arr) => {
                                                const file = arr?.[0] || null;
                                                setNewPurchase((prev) => ({ ...prev, file }));
                                            }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={newPurchase.submittedForReimbursement}
                                                    onChange={(e) =>
                                                        setNewPurchase((prev) => ({
                                                            ...prev,
                                                            submittedForReimbursement: e.target.checked,
                                                        }))
                                                    }
                                                />
                                            }
                                            label="Has this been submitted for reimbursement?"
                                        />
                                    </>
                                )}

                                {/* Error Alert */}
                                {addPurchaseError && (
                                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setAddPurchaseError("")}>
                                        {addPurchaseError}
                                    </Alert>
                                )}

                                {newPurchase.category && newPurchase.category !== "Conferences" && (
                                    <Box display="flex" gap={2} mt={2}>
                                        <Button
                                            variant="contained"
                                            sx={{ backgroundColor: themeColors.teal, color: "#fff", '&:hover': { backgroundColor: themeColors.darkTeal } }}
                                            onClick={() => handleAddPurchase()}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={() => {
                                                setAddingPurchase(false);
                                                resetNewPurchase();
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* FILTERS */}
                <Card sx={{ borderRadius: 2, boxShadow: 1, mb: 2 }}>
                    <CardContent>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h6" fontWeight="600">
                                Filters
                            </Typography>
                            {hasActiveFilters && (
                                <Button
                                    size="small"
                                    onClick={resetFilters}
                                    sx={{ color: themeColors.teal, textTransform: "none" }}
                                >
                                    Clear all
                                </Button>
                            )}
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Category"
                                    value={filters.category}
                                    onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        select: { displayEmpty: true },
                                    }}
                                    sx={{ minWidth: 160 }}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    {categoryOptions.map((c) => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="Date from"
                                    value={filters.dateFrom}
                                    onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="Date to"
                                    value={filters.dateTo}
                                    onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Min cost"
                                    value={filters.costMin}
                                    onChange={(e) => setFilters((f) => ({ ...f, costMin: e.target.value }))}
                                    slotProps={{
                                        htmlInput: { min: 0 },
                                        input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Max cost"
                                    value={filters.costMax}
                                    onChange={(e) => setFilters((f) => ({ ...f, costMax: e.target.value }))}
                                    slotProps={{
                                        htmlInput: { min: 0 },
                                        input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Reimbursement"
                                    value={filters.reimbursement}
                                    onChange={(e) => setFilters((f) => ({ ...f, reimbursement: e.target.value }))}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        select: { displayEmpty: true },
                                    }}
                                    sx={{ minWidth: 180 }}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="submitted">Submitted</MenuItem>
                                    <MenuItem value="not_submitted">Not Submitted</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* TABLE */}
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#FAFAFA" }}>
                                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Cost</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>File</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Reimbursement Status</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPurchases.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {hasActiveFilters ? "No expenses match the selected filters." : "Showing 0 expenses."}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredPurchases.map((p) => (
                                    <TableRow key={p._id} hover>
                                        <TableCell>{p.category}</TableCell>
                                        <TableCell>{p.name || "-"}</TableCell>
                                        <TableCell>{typeof p.cost === "number" ? "$" + p.cost.toFixed(2) : "-"}</TableCell>
                                        <TableCell>{formatDate(p.dateOfPurchase)}</TableCell>
                                        <TableCell>
                                            {p.fileUrl ? (
                                                <Link href={p.fileUrl} target="_blank" sx={{ color: themeColors.teal }}>
                                                    Download
                                                </Link>
                                            ) : (
                                                "-"
                                            )}
                                        </TableCell>
                                        <TableCell>{p.submittedForReimbursement ? "Submitted" : "Not Submitted"}</TableCell>
                                        <TableCell align="center">
                                            <Link
                                                component="button"
                                                sx={{
                                                    color: themeColors.teal,
                                                    mr: 2,
                                                    textDecoration: 'none',
                                                    fontWeight: 500,
                                                    opacity: loadingConferenceId === p._id ? 0.6 : 1,
                                                    cursor: loadingConferenceId === p._id ? 'not-allowed' : 'pointer',
                                                }}
                                                onClick={() => handleEditPurchase(p)}
                                            >
                                                {loadingConferenceId === p._id ? (
                                                    <CircularProgress size={16} />
                                                ) : (
                                                    "Edit"
                                                )}
                                            </Link>
                                            <Link
                                                component="button"
                                                sx={{ color: themeColors.teal, textDecoration: 'none', fontWeight: 500 }}
                                                onClick={() =>
                                                    setDeleteConfirm({
                                                        open: true,
                                                        id: p._id,
                                                        category: p.category,
                                                    })
                                                }
                                            >
                                                Delete
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    {filteredPurchases.length > 0 && (
                        <Box sx={{ p: 2, textAlign: "center", backgroundColor: "#FAFAFA", borderTop: "1px solid #E0E0E0" }}>
                            <Typography variant="body2" color="text.secondary">
                                Showing {filteredPurchases.length}
                                {hasActiveFilters ? " of " + purchases.length : ""} expenses.
                            </Typography>
                        </Box>
                    )}
                </TableContainer>
            </Box>

            {/* NON-CONFERENCE EDIT MODAL */}
            <AddEditPurchaseModal
                open={editOpen}
                handleClose={() => setEditOpen(false)}
                refresh={fetchPurchases}
                initialData={selectedPurchase}
            />

            {/* CONFERENCE ADD MODAL */}
            <ConferenceModal
                open={showConferenceModal}
                onClose={() => {
                    setShowConferenceModal(false);
                    resetNewPurchase();
                }}
                onSave={handleConferenceSave}
                initialPurchase={null}
                categoryFields={categoryFields["Conferences"] || []}
                mode="create"
            />

            {/* CONFERENCE EDIT MODAL */}
            <ConferenceModal
                open={showConferenceEditModal}
                onClose={() => {
                    setShowConferenceEditModal(false);
                    setSelectedPurchase(null);
                }}
                onSave={handleConferenceEditSave}
                onDelete={handleConferenceEditDelete}
                initialPurchase={selectedPurchase}
                categoryFields={categoryFields["Conferences"] || []}
                mode="edit"
            />

            {/* DELETE CONFIRM */}
            <Dialog
                open={deleteConfirm.open}
                onClose={() => setDeleteConfirm({ open: false, id: null, category: null })}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this purchase? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, id: null, category: null })}>
                        Cancel
                    </Button>
                    <Button color="error" onClick={handleDeleteConfirmed}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* YEAR CONFIRMATION */}
            <Dialog
                open={yearConfirm.open}
                onClose={() => {
                    setYearConfirm({ open: false, purchaseYear: null });
                    setPendingConferenceSave(null);
                }}
            >
                <DialogTitle>Purchase Date Outside Current Year</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Your purchase date is outside of the current calendar year. Please note this purchase will not be deducted from this year's annual CME fund.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setYearConfirm({ open: false, purchaseYear: null });
                        setPendingConferenceSave(null);
                    }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: themeColors.teal, color: "#fff" }}
                        onClick={() => {
                            setYearConfirm({ open: false, purchaseYear: null });
                            // Handle both regular purchases and conference purchases
                            if (pendingConferenceSave) {
                                if (pendingConferenceSave.mode === "edit") {
                                    handleConferenceEditSave(pendingConferenceSave, true);
                                } else {
                                    handleConferenceSave(pendingConferenceSave, true);
                                }
                            } else {
                                handleAddPurchase(true);
                            }
                        }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            {/* FIRST-TIME ORIENTATION TOUR */}
            {tourActive && (
                <FundTrackerTour
                    stepIndex={tourStep}
                    categoryOptions={categoryOptions}
                    initialFund={totalFunds}
                    initialRollover={rolloverAmount}
                    spotlightRect={spotlightRect}
                    onNext={handleTourNext}
                    onClose={finishTour}
                    onSaveFund={handleTourSaveFund}
                    onSaveRollover={handleTourSaveRollover}
                    onSelectCategory={handleTourSelectCategory}
                />
            )}
        </>
    );
}
